import { assign, createMachine, State } from 'xstate'
import { interpret, Interpreter } from 'xstate/lib/interpreter'
import { Logger } from '../../../composables/useGtmLogRequest'
import { getDefaultSearchFilters } from '../../search/defaults'
import { FetchCampsiteMapSearchService } from '../infrastructure/FetchCampsiteMapSearchService'
import { FetchCampsitePriceMapSearchService } from '../infrastructure/FetchCampsitePriceMapSearchService'
import {
  mapSearchCampsiteEndpoint,
  mapSearchCampsiteEndpointNonPrice,
  MapSearchContext,
  SearchFilters,
} from '../schemas'
import { generateSnappedGrids } from './grids'

interface MapSearchStateSchema {
  states: {
    idle: {}
    searching: {
      states: {
        setting: {}
        fetching: {}
      }
    }
    results: {}
    error: {}
  }
}

interface GridResult {
  grid: Grid
  campsites: any[]
}

interface ExtendedMapSearchContext extends MapSearchContext {
  searchedGrids: { [key: string]: GridResult }
  keyedResults: { [key: string]: any[] }
  filtersKey: string
}

export type MapSearchStateEvent =
  | { type: 'search'; filters: SearchFilters; signal?: AbortSignal }
  | { type: 'priceSearch'; filters: SearchFilters; signal?: AbortSignal }
  | { type: 'RESULT_RECEIVED'; result: any[] }
  | { type: 'SEARCH_FAILED' }

export const mapSearchMachine = createMachine<
ExtendedMapSearchContext,
MapSearchStateEvent
>(
  {
    id: 'search',
    initial: 'idle',
    context: {
      filters: getDefaultSearchFilters(),
      page: 1,
      languageCode: 'en-gb',
      logger: undefined,
      campsites: [],
      keyedResults: {},
      searchedGrids: {},
      filtersKey: '',
      errorOccurred: false,
    },
    on: {
      search: 'searching',
      priceSearch: 'searching',
      SEARCH_FAILED: {
        actions: assign({
          errorOccurred: (_context, _event) => true,
        }),
      },
      RESULT_RECEIVED: {
        // this gets the markers, dedupes and filters to bounds
        actions: assign((context, event) => {
          const key = context.filtersKey
          const currentResults = context.keyedResults[key] || []
          // Assuming each result has a unique 'id' for deduplication
          const updatedResultsMap = new Map(
            currentResults.map((result) => [result.id, result]),
          )
          // Merge and dedupe
          event.result.forEach((result) => {
            updatedResultsMap.set(result.id, result)
          })
          const mergedAndDedupedResults = Array.from(updatedResultsMap.values())
          // If bounds are specified, filter results by bounds
          const bounds = context.filters.bounds
          const filteredResults = bounds
            ? mergedAndDedupedResults.filter((result) => {
              return (
                result.lat <= bounds.northEast.lat &&
                  result.lat >= bounds.southWest.lat &&
                  result.lng <= bounds.northEast.lng &&
                  result.lng >= bounds.southWest.lng
              )
            })
            : mergedAndDedupedResults
          // Update keyedResults with the new set of filtered, deduplicated results
          const keyedResults = {
            ...context.keyedResults,
            [key]: mergedAndDedupedResults,
          }
          return {
            ...context,
            keyedResults,
            campsites: filteredResults,
            filtersKey: key,
          }
        }),
      },
    },
    states: {
      idle: {},
      searching: {
        initial: 'setting',
        states: {
          setting: {
            entry: assign({
              filters: (_, { filters }) => filters,
              results: (_) => undefined,
              campsites: (_) => [],
              page: (_) => 1,
              errorOccurred: (_) => false,
              filtersKey: (_, { filters }) =>
                JSON.stringify({
                  ...filters,
                  bounds: undefined,
                  base: undefined,
                  cleared: undefined,
                  limit: undefined,
                  page: undefined,
                  previousPath: undefined,
                  previousPoint: undefined,
                  within: undefined,
                }),
            }),
            always: 'fetching',
          },
          fetching: {
            invoke: {
              src: (context) => async (callback) => {
                const { filters, languageCode, logger } = context
                const performSearch = async (searchFilters) => {
                  if (filters.dates) {
                    return await mapSearchCampsiteEndpoint.validate((args) => {
                      const searchService =
                        new FetchCampsitePriceMapSearchService()
                      return searchService.search(args, languageCode, logger, {
                        timeout: 30000,
                      })
                    })(searchFilters)
                  } else {
                    return await mapSearchCampsiteEndpointNonPrice.validate(
                      (args) => {
                        const searchService =
                          new FetchCampsiteMapSearchService()
                        return searchService.search(
                          args,
                          languageCode,
                          logger,
                          { timeout: 30000 },
                        )
                      },
                    )(searchFilters)
                  }
                }
                if (filters.bounds) {
                  const grids = generateSnappedGrids(filters.bounds)
                  const gridSearches = grids.map(async (grid) => {
                    const gridKey = `${grid.bounds.southWest.lat},${grid.bounds.southWest.lng},${grid.bounds.northEast.lat},${grid.bounds.northEast.lng}:${context.filtersKey}`
                    if (context.searchedGrids[gridKey]) {
                      // Grid has been searched before, use stored results
                      const storedResults =
                        context.searchedGrids[gridKey].campsites
                      callback({
                        type: 'RESULT_RECEIVED',
                        result: storedResults,
                      })
                    } else {
                      // Perform search and store results
                      const gridFilters = {
                        ...filters,
                        bounds: grid.bounds,
                        limit: 999999,
                      }
                      await performSearch(gridFilters)
                        .then((result) => {
                          callback({
                            type: 'RESULT_RECEIVED',
                            result: result?.campsites || [],
                          })
                          // Store the grid and its results for future use
                          context.searchedGrids[gridKey] = {
                            grid,
                            campsites: result?.campsites || [],
                          }
                        })
                        .catch((error) => {
                          console.error('Search failed for grid', grid, error)
                          // emit an error that something failed so ui can react
                          callback({ type: 'SEARCH_FAILED' })
                        })
                    }
                  })
                  await Promise.allSettled(gridSearches)
                  return false
                } else {
                  return await performSearch({ ...filters, limit: 99999 })
                }
              },
              onDone: [
                {
                  target: '#search.results',
                  actions: ['setResults'],
                  cond: (_context, event) => !!event.data,
                },
                { target: '#search.results' },
              ],
              onError: [
                {
                  // skip errors from abort
                  target: '#search.idle',
                  cond: (_context, event) => event.data.cause?.code === 20,
                },
                { target: '#search.error' },
              ],
            },
          },
        },
      },
      results: {},
      error: {
        onEntry: ['setError', 'logError'],
        onExit: ['clearError'],
      },
    },
  },
  {
    actions: {
      clearError: assign((_) => {
        return {
          errorMessage: undefined,
          errorOccurred: false,
        }
      }),
      setError: assign((_, { data }: any) => {
        return {
          errorMessage: data.toString(),
          errorOccurred: true,
        }
      }),
      setResults: assign((_, { data }: any) => {
        return {
          results: data,
          campsites: data.campsites,
        }
      }),
      logError: (context, event: any) => {
        console.error(
          'MapsError',
          JSON.stringify(event),
          JSON.stringify(context),
        )
        // event.data.name = 'MapsSearchMachine ' + event.type + ' error'
        // sentryCapture(event.data)
      },
    },
    services: {
      // No additional services defined here as the logic is handled within the states
    },
  },
)

export type MapSearchMachineInterpreter = Interpreter<
ExtendedMapSearchContext,
MapSearchStateSchema,
MapSearchStateEvent
>

export type MapSearchState = State<
ExtendedMapSearchContext,
MapSearchStateEvent
>

export function getMapSearchMachineInterpreted(
  languageCode: string,
  logger: Logger,
) {
  return interpret(
    mapSearchMachine.withContext({
      ...mapSearchMachine.context,
      languageCode,
      logger,
    }),
  )
}
